/**
 * The current season (start year: 2026 is 2026-27), as the backend's schedule
 * says (`GET /api/current_season`, F1 contract §7a). `season` turns over at the
 * scope's first regular-season game, so the preseason keeps last season on
 * show instead of defaulting to a season with no rows yet. `seasonAny` turns
 * over at the first game of any type.
 */
import type { CurrentSeason, ExplorerScope } from "../models/Explorer";
import type { ExplorerAPI } from "./Requests";

const pending = new Map<ExplorerScope, Promise<CurrentSeason>>();
const known = new Map<ExplorerScope, CurrentSeason>();

/**
 * Only for when the route cannot answer (an older API, a network error): the
 * league year's October turnover, which may run a few weeks ahead of `season`.
 */
export function calendarSeasonYear(now: Date = new Date()): number {
  return now.getMonth() >= 9 ? now.getFullYear() : now.getFullYear() - 1;
}

function calendarFallback(scope: ExplorerScope): CurrentSeason {
  const year = calendarSeasonYear();
  return { scope, season: year, seasonAny: year };
}

/** One request per scope per session; callers share it. */
export function loadCurrentSeason(api: ExplorerAPI): Promise<CurrentSeason> {
  const scope = api.scope;
  const inFlight = pending.get(scope);
  if (inFlight) return inFlight;
  const request = api
    .getCurrentSeason()
    .then(({ data }) => {
      if (!Number.isInteger(data?.season)) throw new Error("no season");
      const answer = {
        scope,
        season: data.season,
        seasonAny: Number.isInteger(data.seasonAny) ? data.seasonAny : data.season,
      };
      known.set(scope, answer);
      return answer;
    })
    // Not cached as known: the fallback is a guess, so a later page load asks again.
    .catch(() => calendarFallback(scope));
  pending.set(scope, request);
  return request;
}

/** The answer already fetched this session, for synchronous defaults. */
export function knownCurrentSeason(scope: ExplorerScope): CurrentSeason | undefined {
  return known.get(scope);
}

/** `knownCurrentSeason(scope).season`, else the calendar fallback. */
export function currentSeasonOrGuess(scope: ExplorerScope): number {
  return known.get(scope)?.season ?? calendarSeasonYear();
}

/** Tests only. */
export function resetCurrentSeasons() {
  pending.clear();
  known.clear();
}
